import { MESSAGE_TYPES } from 'hooks/websocket/constants'
import type { WebSocketData } from 'hooks/websocket/types'
import moment from 'moment'
import {
  selectCurrentStreamingMessage,
  setCurrentStreamingMessage,
  updateMessages,
} from 'slices/aiConversations'
import type {
  Callout,
  CalloutAction,
  CalloutKind,
  CalloutLink,
  MonthRange,
} from 'slices/aiConversations/calloutTypes'
import type { Message } from 'slices/aiConversations/types'
import * as uuid from 'uuid'

import type { MessageHandler, MessageHandlerContext } from './types'

const CALLOUT_KINDS: CalloutKind[] = ['info', 'warning', 'error']
const MONTH_FORMAT = 'YYYY-MM-DD'

interface RawCallout {
  id?: unknown
  kind?: unknown
  title?: unknown
  body?: unknown
  links?: unknown
  actions?: unknown
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

// The only hosts outside merchant-ui a callout may link to
const CALLOUT_LINK_HOSTS = [
  'help.thanx.com',
  'docs.thanx.com',
  'thanxwiki.notion.site',
]

// Payload URLs are untrusted; the URL parser reads `/\host` and stray tabs the way the browser will
function resolveUrl(url: string): { to: string } | { href: string } | null {
  let parsed: URL
  try {
    parsed = new URL(url, window.location.origin)
  } catch {
    return null
  }
  if (parsed.origin === window.location.origin) {
    // Dot segments can leave `//host` as the path, which a route renders as a link off-site
    if (parsed.pathname.startsWith('//')) return null
    return { to: `${parsed.pathname}${parsed.search}${parsed.hash}` }
  }
  if (
    parsed.protocol === 'https:' &&
    CALLOUT_LINK_HOSTS.includes(parsed.hostname)
  ) {
    return { href: parsed.href }
  }
  return null
}

const parseMonth = (value: unknown) =>
  moment(isNonEmptyString(value) ? value : null, MONTH_FORMAT, true)

// A malformed or reversed range falls back to the modal's default months
function parseMonthRange(params: unknown): MonthRange | null {
  if (!isRecord(params)) return null
  const start = parseMonth(params.start_month)
  const end = parseMonth(params.end_month)
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) return null
  return {
    start: start.startOf('month').format(MONTH_FORMAT),
    end: end.startOf('month').format(MONTH_FORMAT),
  }
}

function parseLink(link: unknown): CalloutLink | null {
  if (!isRecord(link) || !isNonEmptyString(link.label)) return null
  if (isNonEmptyString(link.url)) {
    const target = resolveUrl(link.url)
    if (!target) return null
    return {
      label: link.label,
      ...target,
      ...(isNonEmptyString(link.permission) && { permission: link.permission }),
    }
  }
  // An unknown modal would open nothing, so its link is dropped; the frontend decides who may open a known one
  if (link.modal === 'purchase_match_rate') {
    return {
      label: link.label,
      modal: 'purchase_match_rate',
      months: parseMonthRange(link.params),
    }
  }
  return null
}

function parseLinks(raw: unknown): CalloutLink[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap(link => {
    const parsed = parseLink(link)
    return parsed ? [parsed] : []
  })
}

// A chip sends the label it shows as the merchant's message; a payload's `message` is never sent
function parseActions(raw: unknown): CalloutAction[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap(action =>
    isRecord(action) && isNonEmptyString(action.label)
      ? [{ label: action.label }]
      : []
  )
}

export function parseCallout(raw: RawCallout): Callout | null {
  if (!isRecord(raw) || !isNonEmptyString(raw.id)) return null

  const title = isNonEmptyString(raw.title) ? raw.title : ''
  const body = isNonEmptyString(raw.body) ? raw.body : ''
  if (!title && !body) return null

  const kind = CALLOUT_KINDS.find(k => k === raw.kind) ?? 'info'

  return {
    id: raw.id,
    kind,
    title,
    body,
    links: parseLinks(raw.links),
    actions: parseActions(raw.actions),
  }
}

function parseContent(content: string | undefined): Callout | null {
  try {
    if (!content) {
      throw new Error('No callout content')
    }
    const callout = parseCallout(JSON.parse(content))
    if (!callout) {
      throw new Error('Callout is missing an id, or a title and a body')
    }
    return callout
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[CalloutHandler] Dropping malformed callout:', error)
    return null
  }
}

// A picker submit adds no user message, so the last message can be an earlier step's picker
function canHoldCallouts(
  message: Message | null | undefined
): message is Message {
  return Boolean(
    message &&
      message.type === 'ai' &&
      !message.isToolCall &&
      !message.pickerData &&
      !message.segmentVisualization &&
      !message.segmentVisualizations?.length &&
      !message.suggestions?.length
  )
}

function withCallout(message: Message, callout: Callout): Message {
  const callouts = message.callouts ?? []
  if (callouts.some(existing => existing.id === callout.id)) {
    return message
  }
  return { ...message, callouts: [...callouts, callout] }
}

export const calloutHandler: MessageHandler = {
  type: MESSAGE_TYPES.CALLOUT,
  handle: (message: WebSocketData, context: MessageHandlerContext) => {
    const callout = parseContent(message.content)
    if (!callout) {
      return
    }

    const state = context.getState()

    const currentStreamingMessage = selectCurrentStreamingMessage(state)
    if (canHoldCallouts(currentStreamingMessage)) {
      context.dispatch(
        setCurrentStreamingMessage(
          withCallout(currentStreamingMessage, callout)
        )
      )
      return
    }

    const currentMessages: Message[] =
      state.aiConversations?.activeConversation?.messages || []
    const lastMessage = currentMessages[currentMessages.length - 1]
    if (canHoldCallouts(lastMessage)) {
      context.dispatch(
        updateMessages(
          currentMessages.map(msg =>
            msg.id === lastMessage.id ? withCallout(msg, callout) : msg
          )
        )
      )
      return
    }

    const calloutMessage: Message = {
      id: uuid.v4(),
      content: '',
      type: 'ai',
      timestamp: new Date(),
      isMarkdown: false,
      sequenceNumber:
        message.sequenceNumber ?? ++context.sequenceCounter.current,
      serverId: message.serverId,
      clientId: uuid.v4(),
      isOptimistic: false,
      callouts: [callout],
    }
    context.addMessageWithOrdering(calloutMessage)
  },
}
